import { HttpClient, HttpErrorResponse, HttpHeaders, HttpRequest, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { catchError, filter, map, Observable, switchMap, tap, throwError } from 'rxjs';
import { AuthStoreService } from '../store/auth-store.service';
import { environment } from 'apps/mnv-seguros-ng/src/environments/environment';
import { Auth } from '../../model/auth.model';

type Method = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';

@Injectable({ providedIn: 'root' })
export abstract class BaseApiService {
  protected apiSegurosUrl = environment.apis.mnvSegurosSb;
  protected apiSeguridadUrl = environment.apis.mnvSeguridadSb;
  protected apiMediadoresUrl = environment.apis.mnvMediadoresSb;
  constructor(protected httpClient: HttpClient, protected authStore: AuthStoreService) { }

  protected invocarSeguros<T>(method: Method, endpoint: string, body?: any): Observable<T> {
    return this.invocarApi<T>(this.apiSegurosUrl + endpoint, method, body, true);
  }

  protected invocarMediadores<T>(method: Method, endpoint: string, body?: any): Observable<T> {
    console.log('ENV:', environment);
    return this.invocarApi<T>(this.apiMediadoresUrl + endpoint, method, body, true);
  }

  protected invocarSeguridad<T>(method: Method, endpoint: string, securizado: boolean, body?: any): Observable<T> {
    return this.invocarApi<T>(this.apiSeguridadUrl + endpoint, method, body, securizado);
  }

  private invocarApi<T>(url: string, method: Method, body: any, securizado: boolean): Observable<T> {
    let headers = new HttpHeaders().set('Content-Type', 'application/json; charset=UTF-8');
    if (securizado) {
      const token = this.authStore.getAccessToken();
      if (!token) {
        this.authStore.logout();
        return throwError(() => new HttpErrorResponse({ status: 401, statusText: 'Token no disponible' }));
      }
      headers = headers.set('Authorization', `Bearer ${token}`);
    }
    const req = new HttpRequest(method, url, body ?? {}, { headers });

    return this.httpClient.request<T>(req).pipe(
      filter((event): event is HttpResponse<T> => event instanceof HttpResponse),
      map((response) => this.extractResponse<T>(response)),
      catchError(error => this.handleHttpError<T>(error, req, securizado))
    );
  }

  private extractResponse<T>(response: HttpResponse<T>): T {
    const body = response.body as any;
    return body && body._embedded;
  }

  private handleHttpError<T>(error: HttpErrorResponse, req: HttpRequest<any>, securizado: boolean): Observable<T> {
    if (error.status === 401 && securizado) {
      return this.callRefreshToken().pipe(
        switchMap(() => {
          const retryRequest = req.clone({
            headers: new HttpHeaders({
              Authorization: `Bearer ${this.authStore.getAccessToken()}`,
              'Content-Type': 'application/json; charset=UTF-8'
            })
          });
          return this.httpClient.request<T>(retryRequest).pipe(
            filter((event): event is HttpResponse<T> => event instanceof HttpResponse),
            map(response => this.extractResponse<T>(response))
          );
        }),
        catchError((e) => {
          // Si el error es 401 después de volver a llamar tras intentar refrescar el token, se cierra la sesión
          if (e.status === 401) {
            this.authStore.logout();
            return throwError(() => new HttpErrorResponse({ status: 401, statusText: 'Sesión expirada' }));
          }
        })
      );
    } else if (error.status === 0) {
      this.authStore.logout();
      return throwError(() => new HttpErrorResponse({ status: 0 }));
    }
    return throwError(() => error);
  }

  private callRefreshToken(): Observable<Auth> {
    const refreshToken = this.authStore.getRefreshToken();
    if (!refreshToken) {
      this.authStore.logout();
      return throwError(() => new HttpErrorResponse({ status: 401, statusText: 'Refresh token no disponible' }));
    }

    // Aquí necesitamos retornar el Observable generado por el pipe()
    return this.refreshToken(refreshToken).pipe(
      tap((auth: Auth) => {
        this.authStore.refreshTokens(auth);
      }),
      catchError((e) => {
        console.error('Error al refrescar el token', e);
        this.authStore.logout();
        return throwError(() => new HttpErrorResponse({ status: 401, statusText: 'Sesión expirada, por favor vuelva a logarse' }));
      })
    );
  }

  private refreshToken(refreshToken: string): Observable<Auth> {
    const requestBody = { refreshToken: refreshToken };
    return this.invocarSeguridad<Auth>('POST', 'auth/refresh', false, requestBody);
  }

}
